import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { JsonViewer } from '@/components/ui/json-viewer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';

interface AuditRow {
  id: string;
  adminUserId: string | null;
  adminEmail: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
}

interface AuditPage {
  data: AuditRow[];
  nextCursor: string | null;
}

const RU_MONTHS = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const month = RU_MONTHS[d.getMonth()];
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${day} ${month}, ${hh}:${mm}:${ss}`;
  } catch {
    return iso;
  }
}

function formatWhenFull(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ru');
  } catch {
    return iso;
  }
}

function shortId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? id.slice(0, 8) : id;
}

function adminLabel(row: AuditRow): string {
  if (row.adminEmail) return row.adminEmail;
  if (row.adminUserId) return row.adminUserId.slice(0, 8);
  return '—';
}

export function AuditListPage() {
  const [action, setAction] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [targetType, setTargetType] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const [dAction, setDAction] = useState('');
  const [dAdminEmail, setDAdminEmail] = useState('');
  const [dTargetType, setDTargetType] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDAction(action), 300);
    return () => clearTimeout(t);
  }, [action]);
  useEffect(() => {
    const t = setTimeout(() => setDAdminEmail(adminEmail), 300);
    return () => clearTimeout(t);
  }, [adminEmail]);
  useEffect(() => {
    const t = setTimeout(() => setDTargetType(targetType), 300);
    return () => clearTimeout(t);
  }, [targetType]);

  const [pages, setPages] = useState<AuditPage[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);

  useEffect(() => {
    setPages([]);
    setCursor(null);
  }, [dAction, dAdminEmail, dTargetType, since, until]);

  const { data, isLoading, isFetching } = useQuery<AuditPage>({
    queryKey: [
      'admin', 'audit',
      dAction, dAdminEmail, dTargetType, since, until, cursor,
    ],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (dAction) qs.set('action', dAction);
      if (dAdminEmail) qs.set('admin_email', dAdminEmail);
      if (dTargetType) qs.set('target_type', dTargetType);
      if (since) qs.set('since', since);
      if (until) qs.set('until', until);
      if (cursor) qs.set('cursor', cursor);
      qs.set('limit', '100');
      return api.get<AuditPage>(`/api/admin/audit?${qs.toString()}`);
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!data) return;
    if (cursor === null) {
      setPages([data]);
    } else {
      setPages((prev) => {
        if (prev[prev.length - 1]?.nextCursor === data.nextCursor && prev.length > 1) {
          return prev;
        }
        if (prev.some((p) => p === data)) return prev;
        return [...prev, data];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const rows: AuditRow[] = pages.flatMap((p) => p.data);
  const lastPage = pages[pages.length - 1];
  const nextCursor = lastPage?.nextCursor ?? null;

  const [selected, setSelected] = useState<AuditRow | null>(null);

  const columns: ColumnDef<AuditRow>[] = [
    {
      id: 'when',
      header: 'Когда',
      cell: ({ row }) => (
        <span className="font-mono text-xs tabular-nums text-zinc-300">
          {formatWhen(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: 'admin',
      header: 'Админ',
      cell: ({ row }) => (
        <span className="text-sm text-zinc-200">{adminLabel(row.original)}</span>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Действие',
      cell: (c) => <StatusBadge status={c.getValue() as string} />,
    },
    {
      accessorKey: 'targetType',
      header: 'Тип',
      cell: (c) => <span className="font-mono text-xs text-zinc-300">{c.getValue() as string}</span>,
    },
    {
      id: 'targetId',
      header: 'ID объекта',
      cell: ({ row }) => {
        const id = row.original.targetId;
        if (!id) return <span className="text-zinc-600">—</span>;
        return (
          <span title={id} className="font-mono text-xs text-zinc-400">
            {shortId(id)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50">Аудит</h1>
          <span className="text-xs text-zinc-500">
            Показано: <span className="font-mono tabular-nums text-zinc-300">{rows.length}</span>
            {isFetching ? <span className="ml-1 text-zinc-600">(обновление…)</span> : null}
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800/80 bg-bg-2 p-3 flex gap-3 items-center flex-wrap">
        <Input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="action (venue.update)"
          className="w-full sm:max-w-[14rem] font-mono text-xs"
        />
        <Input
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="email админа"
          className="w-full sm:max-w-[14rem]"
        />
        <Input
          value={targetType}
          onChange={(e) => setTargetType(e.target.value)}
          placeholder="target_type (venue, user…)"
          className="w-full sm:max-w-[14rem] font-mono text-xs"
        />
        <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <Input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="flex-1 sm:flex-none sm:w-[11rem]"
          />
          <span className="text-zinc-600">→</span>
          <Input
            type="date"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="flex-1 sm:flex-none sm:w-[11rem]"
          />
        </div>
      </div>

      {isLoading && pages.length === 0 ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(r) => setSelected(r)}
            getRowId={(r) => r.id}
            empty="Записей не найдено"
          />
          {nextCursor !== null && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => setCursor(nextCursor)}
                disabled={isFetching}
              >
                {isFetching ? 'Загрузка…' : 'Показать ещё'}
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-w-5xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <StatusBadge status={selected?.action ?? '—'} />
              <span className="text-base font-medium">Запись аудита</span>
            </DialogTitle>
          </DialogHeader>
          {selected ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <DetailRow label="Когда" value={<span className="font-mono tabular-nums">{formatWhenFull(selected.createdAt)}</span>} />
                <DetailRow label="Админ" value={adminLabel(selected)} />
                <DetailRow label="Тип" value={<span className="font-mono">{selected.targetType}</span>} />
                <DetailRow label="ID" value={<span className="font-mono">{selected.targetId ?? '—'}</span>} />
                <DetailRow label="IP" value={<span className="font-mono">{selected.ip ?? '—'}</span>} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Before</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <JsonViewer
                      value={selected.before}
                      className="max-h-[40vh] lg:max-h-[60vh] overflow-y-auto"
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">After</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <JsonViewer
                      value={selected.after}
                      className="max-h-[40vh] lg:max-h-[60vh] overflow-y-auto"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500 w-16">{label}:</span>
      <span className="text-zinc-300">{value}</span>
    </div>
  );
}
